import React from 'react';
import { ProfilesTabParamList, useGoBack } from '../../../../../utils/appNavigationNames';
import { RouteProp } from '@react-navigation/native';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { getIntl } from '../../../../../utils/intl';
import useMedicalProfileFiles from '../../../../../hooks/api/useMedicalProfileFiles';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import Stack from '../../../../v2BaseComponents/Stack';
import SuggestedFolderCard from './SuggestedFolderCard';
import { APITypes } from '../../../../../primary-record-types';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';

type DefaultFolderIds =
    | 'advanced-directives'
    | 'contracts'
    | 'estate-planning'
    | 'finance'
    | 'housing'
    | 'insurance'
    | 'legal'
    | 'medical-equipment'
    | 'plans'
    | 'research'
    | 'schedule';

const defaultFolderIds: DefaultFolderIds[] = [
    'advanced-directives',
    'contracts',
    'estate-planning',
    'finance',
    'housing',
    'insurance',
    'legal',
    'medical-equipment',
    'plans',
    'research',
    'schedule',
];

const intl = getIntl('add-suggested-profile-folders-screen', {
    'advanced-directives': 'Advanced Directives',
    contracts: 'Contracts',
    'estate-planning': 'Estate Planning',
    finance: 'Finance',
    housing: 'Housing',
    insurance: 'Insurance',
    legal: 'Legal',
    'medical-equipment': 'Medical Equipment',
    plans: 'Plans',
    research: 'Research',
    schedule: 'Schedule',
    cancel: 'Cancel',
    addFolders: 'Add',
});

export type AddDefaultProfileFoldersScreenOwnProps = {};

export type AddDefaultProfileFoldersScreenProps = AddDefaultProfileFoldersScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'add-suggested-profile-folders'>;
};

const AddDefaultProfileFoldersScreen: React.FC<AddDefaultProfileFoldersScreenProps> = (props) => {
    const goBack = useGoBack();
    const [selectedFolderIds, setSelectedFolderIds] = React.useState<DefaultFolderIds[]>([]);
    const { bulkCreateFolders, isBulkCreatingFolders } = useMedicalProfileFiles({
        medicalProfileId: props.route.params.profileId,
        onBulkCreateFoldersSuccess: goBack,
    });

    const folderNamesWithIds = defaultFolderIds
        .map((folderId) => ({ id: folderId, name: intl(folderId) }))
        .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <AnimateLoadedContent isLoading={isBulkCreatingFolders}>
            {() => (
                <ActionButtonLayout
                    scrollable
                    actions={[
                        {
                            title: intl('cancel'),
                            onPress: goBack,
                            variant: 'secondary',
                            icon: 'XCircle',
                            isDisabled: isBulkCreatingFolders,
                            testID: 'cancel-add-suggested-folders-button',
                        },
                        {
                            title: intl('addFolders'),
                            onPress: () => {
                                const args = folderNamesWithIds
                                    .filter(({ id }) => selectedFolderIds.includes(id))
                                    .map<APITypes.medicalProfileFilesService.CreateMedicalProfileFileFolderRequest>(
                                        ({ name }) => ({
                                            folderName: name,
                                            parentMedicalProfileFileFolderId: 'root',
                                        }),
                                    );
                                bulkCreateFolders(args);
                            },
                            variant: 'primary',
                            icon: 'CheckCircle',
                            isDisabled: selectedFolderIds.length === 0 || isBulkCreatingFolders,
                            testID: 'add-suggested-folders-button',
                        },
                    ]}
                >
                    <MainContentContainer>
                        <Stack vertical fullWidth space={4}>
                            {folderNamesWithIds.map(({ id, name }) => (
                                <SuggestedFolderCard
                                    key={id}
                                    title={name}
                                    isSelectable
                                    isSelected={selectedFolderIds.includes(id)}
                                    isDisabled={isBulkCreatingFolders}
                                    onSelect={() => {
                                        setSelectedFolderIds((prev) =>
                                            prev.includes(id) ? prev.filter((prevId) => prevId !== id) : [...prev, id],
                                        );
                                    }}
                                />
                            ))}
                        </Stack>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(AddDefaultProfileFoldersScreen);
